import moment from 'moment'
import { useEffect, useReducer, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Chart, Header, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { CALENDAR, PeriodFigures } from './figures'
import { usePeriod, useWindowParams } from './period'
import { useZone } from '~/team_tools/components/timezone'

enum ActionType {
    Loading = 'Loading',
    Loaded = 'Loaded',
}

const reducer = (state, action) => {
    switch (action.type) {
        case ActionType.Loading:
            return { ...state, loading: true }
        case ActionType.Loaded:
            return { ...state, loading: false, data: action.data }
        default:
            throw new Error('Unknow action')
    }
}

export const ActivePlayers = () => {
    const [state, dispatch] = useReducer(reducer, {
        loading: false,
        data: {
            total: undefined,
            history: {},
        },
    })

    // Counted in the zone the shell names: "Today" and every chart day are
    // that zone's days, and switching the picker re-reads them.
    const [zone] = useZone()
    const period = usePeriod()
    const window = useWindowParams()

    const loader = async () => {
        dispatch({ type: ActionType.Loading })
        const resp = await jsonrpc('gameapi.statistic.active_players', {
            tz: zone,
            ...window.figure,
            monthly: period === 'all',
        })
        return resp.result
    }

    useEffect(() => {
        let live = true
        loader().then((data) => live && dispatch({ type: ActionType.Loaded, data }))
        return () => {
            live = false
        }
    }, [zone, window.key])
    return (
        <Box>
            <Header>Active Players</Header>
            <small className='text-white/50'>
                Active means that the player <span className='text-white'>spawned</span> into a match of BR1 Infinite in
                the selected time period. Joining a server without loading in does not count. Fraud-tagged accounts are
                left out.
            </small>
            <Box>
                <PeriodFigures
                    periods={CALENDAR}
                    loading={state.loading}
                    value={(period) => state.data[period]}
                    total={{ label: 'All time', value: state.data.total }}
                    chart={{
                        lines: [{ label: 'Players who spawned', colour: '#f43f5e', history: state.data.history }],
                    }}
                />
            </Box>
            {/* Outside the dashboard there is no window to draw, so the old
                fixed chart stays for those callers. */}
            {!period && (
                <Box.Gray className='w-full'>
                    <ActivePlayersChart history={state.data.history} />
                </Box.Gray>
            )}
        </Box>
    )
}

const ActivePlayersChart = ({ history = {} }) => {
    const [labels, setLabels] = useState<string[]>([])
    const [data, setData] = useState<number[]>([])

    useEffect(() => {
        setLabels(Object.keys(history).map(dayLabel))
        setData(Object.values(history))
    }, [history])

    return (
        <Chart.TimeSeries
            data={{
                labels,
                datasets: [
                    {
                        label: 'Players who spawned',
                        data: [...data],
                        borderColor: '#f43f5e',
                        backgroundColor: '#f43f5e',
                    },
                ],
            }}
        />
    )
}
